import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Activity, BellRing, RotateCcw, Trash2 } from "lucide-react";
import { usersAPI } from "../../api";
import { Avatar, AvatarFallback } from "../ui/avatar";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Separator } from "../ui/separator";
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from "../ui/sheet";
import type { PossibleGarbageUser } from "../../types/possibleGarbage";

type StudentAction = "warn" | "deactivate" | "restore";

// The subset of GET /users/:id this sheet reads. Admins get the full document minus otp/password.
interface StudentDetails {
  phone_number?: string;
  profile_bio?: string;
  study_main?: string;
  graduation_term?: string;
  graduationYear?: string | null;
  is_active?: boolean;
  is_verified?: boolean;
  is_profile_complete?: boolean;
  is_banned?: boolean;
  push_notification_token?: string;
  lastLogin?: string;
  deactivated_reason?: string;
  interest_ids?: { _id: string; name: string }[];
  peers?: unknown[];
  stats?: {
    walksSent?: number;
    eventsCreated?: number;
    eventsAttended?: number;
    spacesJoined?: number;
    ideasCreated?: number;
  };
}

const formatDate = (value?: string | null) => (value ? new Date(value).toLocaleString() : "-");

const Field = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <div>
    <div className="text-xs text-muted-foreground">{label}</div>
    <div className="text-sm break-words">{value || "-"}</div>
  </div>
);

const Stat = ({ label, value }: { label: string; value?: number }) => (
  <div className="rounded-md border p-2 text-center">
    <div className="text-lg font-semibold">{value ?? 0}</div>
    <div className="text-xs text-muted-foreground">{label}</div>
  </div>
);

/**
 * Side panel for one student on the Possible Garbage page: what they filled in,
 * how much they used the app, and where they are in the warn/deactivate flow,
 * so an admin can decide what to do with this one account.
 */
export function StudentDetailsSheet({
  user,
  onClose,
  onAction,
}: {
  user: PossibleGarbageUser | null;
  onClose: () => void;
  onAction: (action: StudentAction) => void;
}) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["possibleGarbageStudent", user?._id],
    queryFn: (): Promise<StudentDetails> => usersAPI.getUserById(user!._id),
    enabled: !!user,
  });

  const initials = `${user?.first_name?.charAt(0) ?? ""}${user?.last_name?.charAt(0) ?? ""}`.toUpperCase() || "?";
  const graduation = [data?.graduation_term, data?.graduationYear].filter(Boolean).join(" ");

  return (
    <Sheet open={user !== null} onOpenChange={(open) => !open && onClose()}>
      <SheetContent className="w-full sm:max-w-md overflow-y-auto">
        {user && (
          <>
            <SheetHeader>
              <div className="flex items-center gap-3">
                <Avatar className="h-12 w-12">
                  <AvatarFallback>{initials}</AvatarFallback>
                </Avatar>
                <div className="min-w-0">
                  <SheetTitle className="truncate">
                    {user.first_name} {user.last_name}
                  </SheetTitle>
                  <SheetDescription className="truncate">{user.email}</SheetDescription>
                </div>
              </div>
            </SheetHeader>

            <div className="space-y-5 px-4">
              <div className="space-y-2">
                <div className="text-sm font-medium">No-photo status</div>
                <div className="flex flex-wrap gap-2">
                  {user.status === "deactivated" && <Badge variant="destructive">Deactivated</Badge>}
                  {user.status === "warned" && <Badge variant="secondary">Warned</Badge>}
                  {user.status === "not_warned" && <Badge variant="outline">Not warned</Badge>}
                  <Badge variant="outline">{user.is_onboarded ? "Finished onboarding" : "Didn't finish onboarding"}</Badge>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Warned at" value={formatDate(user.warned_at)} />
                  <Field label="Deadline" value={formatDate(user.deadline)} />
                  <Field label="Deactivated at" value={formatDate(user.deactivated_at)} />
                  <Field label="Can get the warning push" value={data ? (data.push_notification_token ? "Yes" : "No push token") : "-"} />
                </div>
              </div>

              <Separator />

              {isLoading && <p className="text-sm text-muted-foreground">Loading profile…</p>}
              {isError && <p className="text-sm text-destructive">Couldn't load the full profile.</p>}

              {data && (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="School" value={user.school_name} />
                    <Field label="Major" value={data.study_main} />
                    <Field label="Graduation" value={graduation} />
                    <Field label="Phone" value={data.phone_number} />
                    <Field label="Joined" value={formatDate(user.created_at)} />
                    <Field label="Last active" value={formatDate(user.last_active_at)} />
                    <Field label="Last login" value={formatDate(data.lastLogin)} />
                    <Field label="User ID" value={<span className="font-mono text-xs">{user._id}</span>} />
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <Badge variant={data.is_active ? "default" : "secondary"}>{data.is_active ? "Active" : "Inactive"}</Badge>
                    {data.is_verified && <Badge variant="outline">Verified</Badge>}
                    {data.is_profile_complete && <Badge variant="outline">Profile complete</Badge>}
                    {data.is_banned && <Badge variant="destructive">Banned</Badge>}
                  </div>

                  <Field label="Bio" value={data.profile_bio} />

                  <div>
                    <div className="text-xs text-muted-foreground mb-1">Interests</div>
                    {data.interest_ids?.length ? (
                      <div className="flex flex-wrap gap-1">
                        {data.interest_ids.map((interest) => (
                          <Badge key={interest._id} variant="outline">
                            {interest.name}
                          </Badge>
                        ))}
                      </div>
                    ) : (
                      <div className="text-sm">-</div>
                    )}
                  </div>

                  <div>
                    <div className="text-xs text-muted-foreground mb-1">Activity in the app</div>
                    <div className="grid grid-cols-3 gap-2">
                      <Stat label="Peers" value={data.peers?.length} />
                      <Stat label="Walks sent" value={data.stats?.walksSent} />
                      <Stat label="Events joined" value={data.stats?.eventsAttended} />
                      <Stat label="Events created" value={data.stats?.eventsCreated} />
                      <Stat label="Spaces" value={data.stats?.spacesJoined} />
                      <Stat label="Ideas" value={data.stats?.ideasCreated} />
                    </div>
                  </div>
                </>
              )}

              <Button variant="link" className="px-0" asChild>
                <Link to={`/user-activity/${user._id}`}>
                  <Activity className="h-4 w-4" />
                  Open login and security activity
                </Link>
              </Button>
            </div>

            <SheetFooter className="flex-row flex-wrap">
              {user.status === "deactivated" ? (
                <Button variant="outline" onClick={() => onAction("restore")}>
                  <RotateCcw className="h-4 w-4" />
                  Restore
                </Button>
              ) : (
                <>
                  <Button onClick={() => onAction("warn")}>
                    <BellRing className="h-4 w-4" />
                    Warn
                  </Button>
                  <Button variant="destructive" onClick={() => onAction("deactivate")}>
                    <Trash2 className="h-4 w-4" />
                    Deactivate now
                  </Button>
                </>
              )}
            </SheetFooter>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
